import { AuthData } from "../auth";
import {
  RestFeeds,
  RestUserFeed,
  RestFolderUpdate,
  PostMarker,
  PostStarPatch,
  RestStarList,
  RestUnreadFeed,
  RestNotice,
  RestAnonFeedUpdate,
  ANON_SEMI_CAP,
  FeedFilter,
} from "../../schema-copy/api-schema";
import { getAnonFeeds, getAnonFeedsBatched, pollAnonFeeds } from "../api";
import { mergePostStarPatches, computeAutoReadBoundary, calculateAnonInitialUnread } from "../../schema-copy/readmarker-helper";
import { postCountEngine } from "../postcount-engine";
import { getSinglePost } from "../post-engine";
import { withFeedNameOverride, onFeedNameOverridesChange } from "../feed-name-overrides";
import { logWithTime } from "../../utils/log";
import {
  ReadMarkerEngine,
  SerializedFeedMarker,
  LocalFeedMarker,
  FeedLimits,
  MAX_TRACKED_POSTS,
} from "./read-marker-engine";
import { AnonStore, LocalStorageAnonStore } from "./anon-store";
import {
  FeedEngine,
  ONBOARDING_MAX_UNREAD,
  ONBOARDING_MAX_AGE_HOURS,
  EngineState,
  OfflineMode,
  OfflineChangeCallback,
  StateChangeCallback,
  FeedsChangeCallback,
  StarChangeCallback,
  NoticeChangeCallback,
  LocalNotice,
  applyCurrentFileLocation,
} from "./feed-engine";

// localStorage keys for the anonymous (no-login) session. Everything the logged-in app keeps on
// the server lives here instead.
const ANON_FEEDS_KEY = 'anon-feeds';        // string[] of subscribed feedIds (≤ MAX_ANON_FEEDS)
const ANON_MARKERS_KEY = 'anon-markers';    // SerializedFeedMarker[] (read state)
const ANON_STARRED_KEY = 'anon-starred';    // string[] "feedId-postIndex"
const ANON_READLATER_KEY = 'anon-readlater';// string[] "feedId-postIndex"
const ANON_FOLDERS_KEY = 'anon-folders';    // RestFolderUpdate
const ANON_FEED_META_KEY = 'anon-feed-meta';// Record<feedId, RestUserFeed> (cached metadata)
const ANON_STAR_FEED_META_KEY = 'anon-star-feed-meta';// Record<feedId, RestUserFeed> for starred/read-later feeds you no longer follow

export const MAX_ANON_FEEDS = 30;
/** Anonymous sessions keep at most this many starred and read-later posts each (logged-in
 *  accounts get 1000, enforced server-side). Adds beyond the cap are refused; lists that
 *  exceed it (e.g. written by an older version) are trimmed to the newest entries on load. */
export const MAX_ANON_SAVED_POSTS = 50;
const POLL_INTERVAL_MS = 120_000;

/**
 * Wipe every localStorage entry belonging to the anonymous session — subscriptions, read markers,
 * starred/read-later, folders, cached feed metadata and preferences (all `anon-*` keys). Anonymous
 * mode has no server account, so clearing local storage IS the full data deletion. Used by the
 * settings page's "delete my data" / anonymous "log out" actions.
 */
export function clearAnonData(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith('anon-')) keys.push(k);
    }
    for (const k of keys) localStorage.removeItem(k);
  } catch (e) {
    console.error('clearAnonData failed', e);
  }
}

/**
 * Remove the anon feed library — subscriptions, read markers, starred/read-later, folders and
 * cached feed metadata — but keep the non-library `anon-*` keys (notably `anon-preferences`),
 * so a future anonymous session starts empty but keeps its settings. Used by the post-login
 * import page, where "Import" always clears the feeds but the full wipe is a separate choice.
 */
export function clearAnonFeedData(): void {
  try {
    for (const k of [ANON_FEEDS_KEY, ANON_MARKERS_KEY, ANON_STARRED_KEY, ANON_READLATER_KEY,
        ANON_FOLDERS_KEY, ANON_FEED_META_KEY, ANON_STAR_FEED_META_KEY])
      localStorage.removeItem(k);
  } catch (e) {
    console.error('clearAnonFeedData failed', e);
  }
}

/**
 * Anonymous (no-login) feed engine. Implements the same {@link FeedEngine} contract as the
 * authenticated engine, but the subscription list, read markers, starred, read-later and folders
 * all live in localStorage; feed metadata comes from the public `/api/anon/*` endpoints and post
 * bodies from the CDN. Read markers persist losslessly via the marker engine's serialize/restore
 * (its read state is always a bitmap), and the cross-feed unread list is computed locally from
 * each feed's capped `recentPostsSemi` + the marker engine's read sets.
 */
export class AnonFeedEngine extends FeedEngine {
  private state: EngineState = 'OFF';
  private data: RestFeeds | null = null;
  private markerEngine: ReadMarkerEngine;
  private recentlySubscribed: Map<string, number> = new Map();

  // Per-session /api/anon/feeds call counter: 0 on the first call (which the server counts as a new
  // anonymous "login"), incremented after every call. Fresh on each new engine instance.
  private anonFeedsSeq = 0;

  private feedSemis: Map<string, { semis: number[]; postCount: number; fetchedAt: number }> = new Map();
  private autoReadTimer: ReturnType<typeof setInterval> | null = null;
  private enforceDebounce: Map<string, ReturnType<typeof setTimeout>> = new Map();

  private parsedStarred: Map<string, Set<number>> = new Map();
  private parsedReadLater: Map<string, Set<number>> = new Map();
  private starListFeeds: Map<string, RestUserFeed> = new Map();

  private lastServerTime = 0;
  private nextPollSuggestedTime = Date.now() + POLL_INTERVAL_MS;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;

  private currentNotice: LocalNotice | null = null;

  private stateCallbacks: Set<StateChangeCallback> = new Set();
  private feedsChangeCallbacks: Set<FeedsChangeCallback> = new Set();
  private starChangeCallbacks: Set<StarChangeCallback> = new Set();
  private noticeCallbacks: Set<NoticeChangeCallback> = new Set();
  private offlineCallbacks: Set<OfflineChangeCallback> = new Set();
  private unreadFeedCallbacks: Set<() => void> = new Set();

  private overridesUnsubscribe: (() => void) | null = null;

  // Persistence seam (defaults to localStorage; injectable for tests / a future IndexedDB store).
  constructor(private store: AnonStore = new LocalStorageAnonStore()) {
    super();
    this.markerEngine = new ReadMarkerEngine({
      getAuth: () => null, // no server: the marker engine's sync layer no-ops
      getFeed: (feedId) => this.data?.feeds.find(f => f.id == feedId) ?? this.starListFeeds.get(feedId),
      getFeeds: () => this.data?.feeds ?? [],
      // Every local marker change persists the read state to localStorage.
      broadcastMarkers: () => this.persistMarkers(),
    });
    this.overridesUnsubscribe = onFeedNameOverridesChange(() => this.notifyFeedsChange());
  }

  // --- Persistence ---

  private getFeedIds(): string[] {
    return this.store.getJson<string[]>(ANON_FEEDS_KEY, []).slice(0, MAX_ANON_FEEDS);
  }

  private persistFeedIds(): void {
    this.store.setJson(ANON_FEEDS_KEY, (this.data?.feeds ?? []).map(f => f.id));
  }

  private persistMarkers(): void {
    if (!this.data) return;
    this.store.setJson(ANON_MARKERS_KEY, this.markerEngine.serializeMarkers(this.data.feeds.map(f => f.id)));
  }

  private restoreMarkers(): void {
    const serialized = this.store.getJson<SerializedFeedMarker[]>(ANON_MARKERS_KEY, []);
    if (serialized.length) this.markerEngine.handleBroadcastMessage(serialized);
  }

  private persistStars(): void {
    if (!this.data) return;
    this.store.setJson(ANON_STARRED_KEY, this.data.starred);
    this.store.setJson(ANON_READLATER_KEY, this.data.readLater);
    this.persistStarFeedMeta();
  }

  // Persist metadata for every feed referenced by the starred / read-later lists, so a saved post
  // from a feed you later unsubscribe (and its headline in the saved list) still resolves across
  // reloads. enable() reads these back into starListFeeds, which getFeed consults.
  private persistStarFeedMeta(): void {
    if (!this.data) return;
    const referenced = new Set<string>();
    for (const entry of [...this.data.starred, ...this.data.readLater]) {
      const dash = entry.lastIndexOf('-');
      if (dash > 0) referenced.add(entry.substring(0, dash));
    }
    const meta: Record<string, RestUserFeed> = {};
    referenced.forEach(id => { const f = this.getRawFeed(id); if (f) meta[id] = f; });
    this.store.setJson(ANON_STAR_FEED_META_KEY, meta);
  }

  private loadStarFeedMeta(): void {
    const meta = this.store.getJson<Record<string, RestUserFeed>>(ANON_STAR_FEED_META_KEY, {});
    for (const [id, f] of Object.entries(meta)) if (!this.starListFeeds.has(id)) this.starListFeeds.set(id, f);
  }

  private persistFolders(): void {
    if (!this.data) return;
    this.store.setJson(ANON_FOLDERS_KEY, this.data.folders);
  }

  // --- Lifecycle ---

  async enable(_auth?: AuthData): Promise<void> {
    if (this.state != 'OFF' && this.state != 'ERROR') return;
    this.setState('LOADING');
    try {
      const feedIds = this.getFeedIds();

      // Start from cached feed metadata, so the app works offline AND before the public
      // /api/anon/feeds endpoint is deployed (post content still comes from the CDN via baseUrl).
      const metaCache = this.store.getJson<Record<string, RestUserFeed>>(ANON_FEED_META_KEY, {});
      let feeds: RestUserFeed[] = feedIds.map(id => metaCache[id]).filter((f): f is RestUserFeed => !!f);
      let serverTime = Date.now();
      let notice: RestNotice | undefined;
      if (feedIds.length) {
        try {
          const result = await getAnonFeeds(feedIds, this.anonFeedsSeq++);
          if (result.feeds.length) feeds = result.feeds; // fresh metadata wins when available
          serverTime = result.serverTime;
          notice = result.notice;
        } catch (e) {
          console.warn('anon: /api/anon/feeds unavailable; using cached feed metadata', e);
        }
      }

      const folders = this.store.getJson<RestFolderUpdate>(ANON_FOLDERS_KEY, {});
      // Newest entries are at the end, so trimming keeps the newest (mirrors the server cap).
      const starred = this.store.getJson<string[]>(ANON_STARRED_KEY, []).slice(-MAX_ANON_SAVED_POSTS);
      const readLater = this.store.getJson<string[]>(ANON_READLATER_KEY, []).slice(-MAX_ANON_SAVED_POSTS);

      this.data = {
        feeds,
        folders: this.healFolders(folders, feeds),
        feedMarkers: [],
        bitmaps: [],
        starred,
        readLater,
        starredUpdate: new Date(0).toISOString(),
        readLaterUpdate: new Date(0).toISOString(),
        lastUpdate: new Date(serverTime).toISOString(),
        serverTime,
        discoverDate: '',
        preferences: {},
      } as RestFeeds;

      this.applyNotice(notice);
      this.updatePostCountCache(feeds);
      // Create an empty marker for every feed (so per-feed mutations have an entry to update),
      // then overlay the persisted read state from localStorage on top.
      this.markerEngine.loadFromData([], [], feeds, serverTime);
      this.restoreMarkers();
      this.seedFeedSemisAndEnforce(feeds);
      this.loadStarFeedMeta();
      this.parseStarData();
      this.persistFeedMeta();
      this.lastServerTime = serverTime;
      this.setState('READY');
      this.notifyFeedsChange();
      this.schedulePoll();
    } catch (e) {
      console.error('anon: enable failed', e);
      this.setState('ERROR');
    }
  }

  // Persist the current feeds' metadata so a reload (or a session before /api/anon/feeds is
  // deployed) can render them without a network round-trip.
  private persistFeedMeta(): void {
    if (!this.data) return;
    const meta: Record<string, RestUserFeed> = {};
    for (const f of this.data.feeds) meta[f.id] = f;
    this.store.setJson(ANON_FEED_META_KEY, meta);
  }

  // Append any subscribed feed missing from the folder structure to the unfiled ('') bucket.
  private healFolders(folders: RestFolderUpdate, feeds: RestUserFeed[]): RestFolderUpdate {
    const placed = new Set<string>();
    for (const ids of Object.values(folders)) for (const id of ids) placed.add(id);
    const missing = feeds.map(f => f.id).filter(id => !placed.has(id));
    if (!missing.length) return folders;
    const healed: RestFolderUpdate = { ...folders };
    healed[''] = [...(healed[''] ?? []), ...missing];
    return healed;
  }

  disable(): void {
    this.stopPoll();
    this.markerEngine.clear();
    this.data = null;
    this.currentNotice = null;
    this.feedSemis.clear();
    if (this.autoReadTimer) { clearInterval(this.autoReadTimer); this.autoReadTimer = null; }
    this.enforceDebounce.forEach(t => clearTimeout(t));
    this.enforceDebounce.clear();
    this.parsedStarred.clear();
    this.parsedReadLater.clear();
    this.starListFeeds.clear();
    this.recentlySubscribed.clear();
    this.lastServerTime = 0;
    this.setState('OFF');
  }

  destroy(): void {
    this.disable();
    this.stateCallbacks.clear();
    this.feedsChangeCallbacks.clear();
    this.starChangeCallbacks.clear();
    this.noticeCallbacks.clear();
    this.offlineCallbacks.clear();
    this.unreadFeedCallbacks.clear();
    this.overridesUnsubscribe?.();
    this.overridesUnsubscribe = null;
  }

  pause(): void {
    if (this.state == 'READY' || this.state == 'UPDATING' || this.state == 'INTERRUPTED') {
      this.stopPoll();
      this.setState('PAUSED');
    }
  }

  async resume(): Promise<void> {
    if (this.state != 'PAUSED') return;
    this.setState('READY');
    await this.poll();
  }

  async reloadData(): Promise<void> {
    if (this.state == 'OFF') return;
    // A reload is just a fresh enable from a clean slate.
    this.disable();
    await this.enable();
  }

  // --- Polling ---

  private schedulePoll(): void {
    this.stopPoll();
    if (this.state != 'READY' && this.state != 'INTERRUPTED') return;
    const delay = Math.max(0, this.nextPollSuggestedTime - Date.now());
    this.pollTimer = setTimeout(() => { this.poll(); }, delay);
  }

  private stopPoll(): void {
    if (this.pollTimer) { clearTimeout(this.pollTimer); this.pollTimer = null; }
  }

  async pollNow(): Promise<void> {
    if (this.state == 'READY') await this.poll();
  }

  private async poll(): Promise<void> {
    if (!this.data) return;
    if (this.state != 'READY' && this.state != 'INTERRUPTED') return;

    const now = Date.now();
    this.recentlySubscribed.forEach((ts, id) => { if (now - ts > 60_000) this.recentlySubscribed.delete(id); });

    const feedIds = this.data.feeds.map(f => f.id);
    if (!feedIds.length) { this.nextPollSuggestedTime = Date.now() + POLL_INTERVAL_MS; this.schedulePoll(); return; }

    this.setState('UPDATING');
    try {
      logWithTime('anon: polling for feed updates');
      const result = await pollAnonFeeds(feedIds, this.lastServerTime);
      this.applyNotice(result.notice);
      this.applyPollResult(result.updates);
      this.lastServerTime = result.serverTime;
      this.nextPollSuggestedTime = Date.now() + (result.nextPollSuggestedTime - result.serverTime);
      this.setState('READY');
      this.schedulePoll();
    } catch (e) {
      console.error('anon: poll failed', e);
      this.setState('INTERRUPTED');
      this.nextPollSuggestedTime = Date.now() + POLL_INTERVAL_MS;
      this.schedulePoll();
    }
  }

  private applyPollResult(updates: Record<string, RestAnonFeedUpdate>): void {
    if (!this.data) return;
    let changed = false;
    for (const [feedId, update] of Object.entries(updates)) {
      const feed = this.data.feeds.find(f => f.id == feedId);
      if (!feed) continue;
      const oldPostCount = feed.postCount;
      feed.postCount = update.postCount;
      feed.lastNewPostAtSemi = update.lastNewPostAtSemi;
      feed.feedStatus = update.feedStatus;
      applyCurrentFileLocation(feed, update);
      // Merge the newest post-times so the unread list can place the new posts.
      if (update.recentPostsSemi?.length) {
        feed.recentPostsSemi = update.recentPostsSemi.slice(0, ANON_SEMI_CAP);
        this.feedSemis.set(feedId, { semis: feed.recentPostsSemi, postCount: feed.postCount, fetchedAt: Date.now() });
      }
      const newPostCount = feed.postCount;
      this.markerEngine.handlePollPostCountUpdate(feedId, oldPostCount, newPostCount);
      this.enforceAutoRead(feedId);
      changed = true;
    }
    if (changed) {
      this.updatePostCountCache(this.data.feeds);
      this.persistFeedMeta();
      this.notifyFeedsChange();
      this.unreadFeedCallbacks.forEach(cb => { try { cb(); } catch (e) { console.error(e); } });
    }
  }

  private updatePostCountCache(feeds: RestUserFeed[]): void {
    const postCounts: Record<string, number> = {};
    for (const feed of feeds) postCounts[feed.id] = feed.postCount;
    postCountEngine.updatePostCounts(postCounts);
  }

  // --- State + callbacks ---

  private setState(newState: EngineState): void {
    if (this.state == newState) return;
    const prev = this.state;
    this.state = newState;
    this.stateCallbacks.forEach(cb => { try { cb(newState, prev); } catch (e) { console.error('State callback error:', e); } });
  }

  private notifyFeedsChange(): void {
    if (!this.data) return;
    const feeds = this.data.feeds.map(withFeedNameOverride);
    this.feedsChangeCallbacks.forEach(cb => { try { cb(feeds, this.data!.folders); } catch (e) { console.error('Feeds change callback error:', e); } });
  }

  private notifyStarChange(): void {
    const starred = this.getStarredCount();
    const readLater = this.getReadLaterCount();
    this.starChangeCallbacks.forEach(cb => { try { cb(starred, readLater); } catch (e) { console.error(e); } });
  }

  private applyNotice(notice: RestNotice | undefined): void {
    if (!notice) {
      if (this.currentNotice) { this.currentNotice = null; this.noticeCallbacks.forEach(cb => cb(null)); }
      return;
    }
    const local: LocalNotice = {
      message: notice.message,
      shutdownAtMs: notice.secondsTillShutdown != null ? Date.now() + notice.secondsTillShutdown * 1000 : undefined,
    };
    this.currentNotice = local;
    this.noticeCallbacks.forEach(cb => cb(local));
  }

  onStateChange(callback: StateChangeCallback): () => void { this.stateCallbacks.add(callback); return () => this.stateCallbacks.delete(callback); }
  onUnreadChange = (callback: Parameters<ReadMarkerEngine['onUnreadChange']>[0]): (() => void) => this.markerEngine.onUnreadChange(callback);
  onFeedsChange(callback: FeedsChangeCallback): () => void { this.feedsChangeCallbacks.add(callback); return () => this.feedsChangeCallbacks.delete(callback); }
  onStarChange(callback: StarChangeCallback): () => void { this.starChangeCallbacks.add(callback); return () => this.starChangeCallbacks.delete(callback); }
  onNoticeChange(callback: NoticeChangeCallback): () => void { this.noticeCallbacks.add(callback); return () => this.noticeCallbacks.delete(callback); }
  onOfflineChange(callback: OfflineChangeCallback): () => void { this.offlineCallbacks.add(callback); return () => this.offlineCallbacks.delete(callback); }
  onUnreadFeedChange(callback: () => void): () => void { this.unreadFeedCallbacks.add(callback); return () => this.unreadFeedCallbacks.delete(callback); }

  // Anonymous mode never enters an offline state (no server session to lose); these satisfy the contract.
  getOfflineMode(): OfflineMode { return 'none'; }
  isOffline(): boolean { return false; }
  getNotice(): LocalNotice | null { return this.currentNotice; }

  getState(): EngineState { return this.state; }
  getData(): RestFeeds | null { return this.data; }
  getDiscoverDate(): string | undefined { return this.data?.discoverDate || undefined; }
  async waitForDiscoverDate(): Promise<string> { return this.data?.discoverDate ?? ''; }

  // --- Read marker delegations ---

  getSortOrder(feedId: string): 'N' | 'O' | 'A' | '?' { return this.markerEngine.getSortOrder(feedId); }
  setSortOrder(feedId: string, order: 'N' | 'O' | 'A'): void { this.markerEngine.setSortOrder(feedId, order); }
  getShowRead(feedId: string): 'u' | 'a' { return this.markerEngine.getShowRead(feedId); }
  setShowRead(feedId: string, showRead: 'u' | 'a'): void { this.markerEngine.setShowRead(feedId, showRead); }
  getCompactMode(feedId: string): 'c' | 'f' | 'l' { return this.markerEngine.getCompactMode(feedId); }
  setCompactMode(feedId: string, mode: 'c' | 'f' | 'l'): void { this.markerEngine.setCompactMode(feedId, mode); }
  getFeedMarker(feedId: string): LocalFeedMarker | undefined { return this.markerEngine.getFeedMarker(feedId); }
  getAllFeedMarkers(): Map<string, LocalFeedMarker> { return this.markerEngine.getAllFeedMarkers(); }
  isPostRead(feedId: string, postIndex: number): boolean { return this.markerEngine.isPostRead(feedId, postIndex); }
  getReadForFeed(feedId: string): Set<number> { return this.markerEngine.getReadForFeed(feedId); }
  getUnreadCount(feedId: string): number { return this.markerEngine.getUnreadCount(feedId); }
  markPostRead(feedId: string, postIndex: number): void { this.markerEngine.markPostRead(feedId, postIndex); }
  markPostUnread(feedId: string, postIndex: number): void { this.markerEngine.markPostUnread(feedId, postIndex); }
  markPostsRead(feedId: string, postIndices: PostMarker[]): void { this.markerEngine.markPostsRead(feedId, postIndices); }
  markAllRead(feedId: string): void { this.markerEngine.markAllRead(feedId); }
  markAllUnread(feedId: string): void { this.markerEngine.markAllUnread(feedId); }
  preloadFeed(feedId: string, count = 10): void { this.markerEngine.preloadFeed(feedId, count); }

  markOlderThanDaysRead(feedId: string, days: number): Promise<void> { return this.markOlderThanKeepRead(feedId, days, 0); }

  async markOlderThanKeepRead(feedId: string, days: number, keep: number): Promise<void> {
    const feed = this.getFeed(feedId);
    if (!feed) return;
    const postCount = feed.postCount;
    if (!postCount) return;
    const keepBoundary = postCount - keep - 1;
    if (keepBoundary < 0) return;
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    let lo = 0, hi = postCount - 1, olderBoundary = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const post = await getSinglePost(feed, mid).catch(() => undefined);
      const t = post?.pubDate ? new Date(post.pubDate).getTime() : 0;
      if (t < cutoff) { olderBoundary = mid; lo = mid + 1; } else hi = mid - 1;
    }
    const boundary = Math.min(olderBoundary, keepBoundary);
    if (boundary >= 0) this.markerEngine.markPostsRead(feedId, [[0, boundary]]);
  }

  // --- Auto-mark-read limits ---

  getFeedLimits(feedId: string): FeedLimits { return this.markerEngine.getFeedLimits(feedId); }

  setFeedLimits(feedId: string, maxUnread: number, maxAgeDays: number): void {
    this.markerEngine.setFeedLimits(feedId, maxUnread, maxAgeDays);
    const pending = this.enforceDebounce.get(feedId);
    if (pending) clearTimeout(pending);
    this.enforceDebounce.set(feedId, setTimeout(() => {
      this.enforceDebounce.delete(feedId);
      this.enforceAutoRead(feedId);
    }, 1500));
  }

  // The anon feeds load usually ships recentPostsSemi (≤100) per feed, seeded into feedSemis. But a
  // feed registered from a catalog object — or loaded before /api/anon/feeds was reachable — can lack
  // it, and without a snapshot the limits-preview spins on "Calculating…" forever (previewAutoRead
  // returns undefined when an age limit is set but there's no snapshot). Ensure one exists: best-effort
  // refresh from /api/anon/feeds, then always set an entry (semis may be empty) so the preview resolves.
  async ensureFeedSemis(feedId: string): Promise<void> {
    const cached = this.feedSemis.get(feedId);
    if (cached && Date.now() - cached.fetchedAt < 15 * 60_000) return;
    const feed = this.getRawFeed(feedId);
    if (!feed) return;
    if (!feed.recentPostsSemi?.length) {
      try {
        const fetched = (await getAnonFeedsBatched([feedId], this.anonFeedsSeq++)).feeds[0];
        if (fetched?.recentPostsSemi?.length) {
          feed.recentPostsSemi = fetched.recentPostsSemi.slice(0, ANON_SEMI_CAP);
          feed.postCount = fetched.postCount || feed.postCount;
        }
      } catch (e) {
        console.warn('anon: ensureFeedSemis fetch unavailable', e);
      }
    }
    this.feedSemis.set(feedId, {
      semis: feed.recentPostsSemi ?? [],
      postCount: feed.postCount,
      fetchedAt: Date.now(),
    });
  }

  previewAutoRead(feedId: string, maxUnread: number, maxAgeDays: number): number | undefined {
    const feed = this.getFeed(feedId);
    const basis = feed?.postCount ?? 0;
    if (!basis) return 0;
    const snap = this.feedSemis.get(feedId);
    if (maxAgeDays && !snap) return undefined;
    const boundary = computeAutoReadBoundary(basis, snap?.semis, snap?.postCount ?? basis, maxUnread, maxAgeDays, Date.now());
    if (boundary < 0) return 0;
    return this.markerEngine.countUnreadAtOrBelow(feedId, boundary);
  }

  // Keyword filters are a logged-in pro/vip feature; the anon engine has none.
  getFeedFilter(): FeedFilter | undefined {
    return undefined;
  }

  async setFeedFilter(): Promise<void> {}

  async previewFeedFilter(): Promise<number> {
    return 0;
  }

  private enforceAutoRead(feedId: string): void {
    const { maxUnread, maxAgeDays } = this.markerEngine.getFeedLimits(feedId);
    if (!maxUnread && !maxAgeDays) return;
    const feed = this.data?.feeds.find(f => f.id == feedId);
    const basis = feed?.postCount ?? 0;
    if (!basis) return;
    const snap = this.feedSemis.get(feedId);
    const boundary = computeAutoReadBoundary(basis, snap?.semis, snap?.postCount ?? basis, maxUnread, maxAgeDays, Date.now());
    if (boundary < 0) return;
    if (!this.markerEngine.countUnreadAtOrBelow(feedId, boundary)) return;
    const start = Math.max(0, basis - MAX_TRACKED_POSTS);
    this.markerEngine.markPostsRead(feedId, [[start, boundary]]);
  }

  private enforceAutoReadAll(): void {
    this.markerEngine.getAllFeedMarkers().forEach((marker, feedId) => {
      if (marker.maxUnread || marker.maxAgeDays) this.enforceAutoRead(feedId);
    });
  }

  private seedFeedSemisAndEnforce(feeds: RestUserFeed[]): void {
    const now = Date.now();
    for (const feed of feeds)
      if (feed.recentPostsSemi)
        this.feedSemis.set(feed.id, { semis: feed.recentPostsSemi, postCount: feed.postCount, fetchedAt: now });
    this.enforceAutoReadAll();
    if (!this.autoReadTimer)
      this.autoReadTimer = setInterval(() => this.enforceAutoReadAll(), 15 * 60_000);
  }

  // --- Feeds / folders ---

  getFeeds(): RestUserFeed[] { return (this.data?.feeds ?? []).map(withFeedNameOverride); }

  getFeed(feedId: string): RestUserFeed | undefined {
    const raw = this.data?.feeds.find(f => f.id == feedId) ?? this.starListFeeds.get(feedId);
    return raw && withFeedNameOverride(raw);
  }

  getRawFeed(feedId: string): RestUserFeed | undefined {
    return this.data?.feeds.find(f => f.id == feedId) ?? this.starListFeeds.get(feedId);
  }

  registerFeedInfo(feed: RestUserFeed): void { this.starListFeeds.set(feed.id, feed); }

  async ensureFeedInfo(feedId: string): Promise<RestUserFeed | undefined> {
    const feed = this.getFeed(feedId);
    if (feed) return feed;
    // Unsubscribed feeds aren't tracked server-side for anon; best effort fetch its public metadata.
    try {
      const result = await getAnonFeedsBatched([feedId], this.anonFeedsSeq++);
      const fetched = result.feeds[0];
      if (fetched) { this.starListFeeds.set(feedId, fetched); return withFeedNameOverride(fetched); }
    } catch (e) {
      console.error('anon: ensureFeedInfo failed', e);
    }
    return undefined;
  }

  getFolders(): RestFolderUpdate { return this.data?.folders ?? {}; }

  // --- Subscriptions ---

  async subscribe(feedId: string): Promise<RestUserFeed | null> {
    if (!this.data) return null;
    const existing = this.data.feeds.find(f => f.id == feedId);
    if (existing) return existing;

    if (this.data.feeds.length >= MAX_ANON_FEEDS) {
      console.warn(`anon: reached the ${MAX_ANON_FEEDS}-feed limit`);
      return null;
    }

    let feed = this.starListFeeds.get(feedId);
    if (!feed) {
      try { feed = (await getAnonFeedsBatched([feedId], this.anonFeedsSeq++)).feeds[0]; } catch (e) { console.error('anon: subscribe fetch failed', e); }
    }
    if (!feed) return null;

    this.data.feeds.push(feed);
    // A freshly-followed feed starts all-unread (mode '0', empty bitmap).
    this.markerEngine.handleSubscribeResult(feed, `${feed.id}/A/uf/0`, '');
    this.applyInitialReadLimit(feed);
    this.persistFeedIds();
    this.persistFeedMeta();
    this.markRecentlySubscribed(feedId);
    this.updatePostCountCache(this.data.feeds);
    if (feed.recentPostsSemi)
      this.feedSemis.set(feed.id, { semis: feed.recentPostsSemi, postCount: feed.postCount, fetchedAt: Date.now() });
    this.notifyFeedsChange();
    return feed;
  }

  // Anonymous subscriptions live only in this browser, so there is no server state to adopt: the
  // feed is either already in the local list or nothing can be done about it.
  async adoptExistingSubscription(feedId: string): Promise<RestUserFeed | undefined> {
    return this.data?.feeds.find(f => f.id == feedId);
  }

  handleSubscribeResult(result: { feed: RestUserFeed; feedMarker: string; bitmap: string }): void {
    if (!this.data) return;
    const { feed } = result;
    if (this.data.feeds.find(f => f.id == feed.id)) return;
    if (this.data.feeds.length >= MAX_ANON_FEEDS) return;
    this.data.feeds.push(feed);
    this.markerEngine.handleSubscribeResult(feed, `${feed.id}/A/uf/0`, '');
    this.applyInitialReadLimit(feed);
    this.persistFeedIds();
    this.persistFeedMeta();
    this.markRecentlySubscribed(feed.id);
    this.updatePostCountCache(this.data.feeds);
    this.notifyFeedsChange();
  }

  async unsubscribe(feedId: string): Promise<boolean> {
    if (!this.data) return false;
    // Keep the feed's metadata resolvable for any starred / read-later post that still references it
    // (otherwise its headline in the saved list would spin forever waiting on getFeed).
    const removed = this.data.feeds.find(f => f.id == feedId);
    if (removed) this.starListFeeds.set(feedId, removed);
    this.data.feeds = this.data.feeds.filter(f => f.id != feedId);
    this.markerEngine.handleUnsubscribe(feedId);
    this.recentlySubscribed.delete(feedId);
    this.feedSemis.delete(feedId);
    const pending = this.enforceDebounce.get(feedId);
    if (pending) { clearTimeout(pending); this.enforceDebounce.delete(feedId); }
    for (const feedIds of Object.values(this.data.folders)) {
      const idx = feedIds.indexOf(feedId);
      if (idx >= 0) feedIds.splice(idx, 1);
    }
    this.persistFeedIds();
    this.persistFeedMeta();
    this.persistMarkers();
    this.persistFolders();
    this.persistStarFeedMeta();
    this.notifyFeedsChange();
    return true;
  }

  // On first subscribe, mark the older posts read so a long-running feed doesn't arrive with
  // hundreds of unread items: keep at least the 10 newest unread, plus everything from the last
  // 72h, capped at 100. Shared math lives in the base engine's applyStarterLimitWith.
  private applyInitialReadLimit(feed: RestUserFeed): void {
    this.applyStarterLimitWith(feed.id, feed.recentPostsSemi, feed.postCount);
  }

  async applyStarterReadLimit(feedId: string): Promise<void> {
    const feed = this.getRawFeed(feedId);
    if (feed) this.applyStarterLimitWith(feedId, feed.recentPostsSemi, feed.postCount, ONBOARDING_MAX_UNREAD, ONBOARDING_MAX_AGE_HOURS);
  }

  private markRecentlySubscribed(feedId: string): void {
    this.recentlySubscribed.set(feedId, Date.now());
    setTimeout(() => {
      const ts = this.recentlySubscribed.get(feedId);
      if (ts != undefined && Date.now() - ts >= 60_000) { this.recentlySubscribed.delete(feedId); this.notifyFeedsChange(); }
    }, 60_500);
  }

  isRecentlySubscribed(feedId: string): boolean {
    const ts = this.recentlySubscribed.get(feedId);
    if (!ts) return false;
    if (Date.now() - ts > 60_000) { this.recentlySubscribed.delete(feedId); return false; }
    return true;
  }

  // Anon subscribes never trigger a server fetch, so there is no confirmed refresh — keep the
  // old recent-subscribe heuristic for the loading spinner.
  isRefreshing(feedId: string): boolean { return this.isRecentlySubscribed(feedId); }

  getPrivateFeedCount(): number { return this.data?.feeds.filter(f => f.feedStatus == 'PRIVATE').length ?? 0; }
  getNewsletterFeedCount(): number { return this.data?.feeds.filter(f => f.feedStatus == 'NEWSLETTER').length ?? 0; }

  // --- Folder management (all local; no server) ---

  private commitFolders(newFolders: RestFolderUpdate): boolean {
    if (!this.data) return false;
    this.data.folders = newFolders;
    this.persistFolders();
    this.notifyFeedsChange();
    return true;
  }

  async setFeedFolder(feedId: string, folderName: string | null): Promise<boolean> { return this.moveFeed(feedId, folderName); }

  async moveFeed(feedId: string, folderName: string | null, position?: number): Promise<boolean> {
    if (!this.data) return false;
    const targetKey = folderName ?? '';
    const newFolders: RestFolderUpdate = {};
    for (const [key, feedIds] of Object.entries(this.data.folders)) newFolders[key] = feedIds.filter(id => id != feedId);
    if (!newFolders[targetKey]) newFolders[targetKey] = [];
    if (!targetKey) {
      const placed = new Set(Object.values(newFolders).flat());
      for (const feed of this.getFeeds()) if (feed.id != feedId && !placed.has(feed.id)) newFolders[targetKey].push(feed.id);
    }
    if (position != null) newFolders[targetKey].splice(position, 0, feedId);
    else newFolders[targetKey].push(feedId);
    return this.commitFolders(newFolders);
  }

  async moveFeeds(feedIds: string[], folderName: string | null, position?: number): Promise<boolean> {
    if (!this.data || !feedIds.length) return false;
    const targetKey = folderName ?? '';
    const moving = new Set(feedIds);
    const newFolders: RestFolderUpdate = {};
    for (const [key, ids] of Object.entries(this.data.folders)) newFolders[key] = ids.filter(id => !moving.has(id));
    if (!newFolders[targetKey]) newFolders[targetKey] = [];
    const at = position != null ? Math.min(Math.max(position, 0), newFolders[targetKey].length) : newFolders[targetKey].length;
    newFolders[targetKey].splice(at, 0, ...feedIds);
    return this.commitFolders(newFolders);
  }

  async addEmptyFolder(folderName: string): Promise<boolean> {
    if (!this.data || this.data.folders[folderName]) return false;
    return this.commitFolders({ ...this.data.folders, [folderName]: [] });
  }

  async deleteFolder(folderName: string, deleteFeeds: boolean): Promise<boolean> {
    if (!this.data) return false;
    const feedIds = this.data.folders[folderName];
    if (!feedIds) return false;
    if (deleteFeeds && feedIds.length) for (const feedId of [...feedIds]) await this.unsubscribe(feedId);
    const newFolders: RestFolderUpdate = { ...this.data.folders };
    if (!deleteFeeds && feedIds.length) newFolders[''] = [...feedIds, ...(newFolders[''] ?? [])];
    delete newFolders[folderName];
    return this.commitFolders(newFolders);
  }

  async renameFolder(oldName: string, newName: string): Promise<boolean> {
    if (!this.data) return false;
    const trimmed = newName.trim();
    if (!trimmed || trimmed == oldName || !(oldName in this.data.folders) || trimmed in this.data.folders) return false;
    const newFolders: RestFolderUpdate = {};
    for (const [key, value] of Object.entries(this.data.folders)) newFolders[key == oldName ? trimmed : key] = value;
    return this.commitFolders(newFolders);
  }

  async setFolderOrder(orderedNames: string[]): Promise<boolean> {
    if (!this.data) return false;
    const current = this.data.folders;
    const newFolders: RestFolderUpdate = {};
    if (current[''] !== undefined) newFolders[''] = current[''];
    for (const name of orderedNames) if (name != '' && current[name] !== undefined) newFolders[name] = current[name];
    for (const [key, value] of Object.entries(current)) if (!(key in newFolders)) newFolders[key] = value;
    return this.commitFolders(newFolders);
  }

  async mergeFolders(source: string, target: string): Promise<boolean> {
    if (!this.data || source == target) return false;
    const src = this.data.folders[source];
    const tgt = this.data.folders[target];
    if (!src || !tgt) return false;
    const newFolders: RestFolderUpdate = { ...this.data.folders };
    newFolders[target] = [...tgt, ...src];
    delete newFolders[source];
    return this.commitFolders(newFolders);
  }

  // --- Starred / read-later (all local; no server) ---

  isStarred(feedId: string, postIndex: number): boolean { return this.parsedStarred.get(feedId)?.has(postIndex) ?? false; }
  isReadLater(feedId: string, postIndex: number): boolean { return this.parsedReadLater.get(feedId)?.has(postIndex) ?? false; }
  getStarredForFeed(feedId: string): Set<number> { return this.parsedStarred.get(feedId) ?? new Set(); }
  getReadLaterForFeed(feedId: string): Set<number> { return this.parsedReadLater.get(feedId) ?? new Set(); }
  getStarredEntries(): string[] { return this.data?.starred ?? []; }
  getReadLaterEntries(): string[] { return this.data?.readLater ?? []; }

  private countStarMap(map: Map<string, Set<number>>): number { let n = 0; map.forEach(s => n += s.size); return n; }
  getStarredCount(): number { return this.countStarMap(this.parsedStarred); }
  getReadLaterCount(): number { return this.countStarMap(this.parsedReadLater); }

  starPost(feedId: string, postIndex: number): boolean { return this.setStar('starred', this.parsedStarred, feedId, postIndex, true); }
  unstarPost(feedId: string, postIndex: number): void { this.setStar('starred', this.parsedStarred, feedId, postIndex, false); }
  markReadLater(feedId: string, postIndex: number): boolean { return this.setStar('readLater', this.parsedReadLater, feedId, postIndex, true); }
  unmarkReadLater(feedId: string, postIndex: number): void { this.setStar('readLater', this.parsedReadLater, feedId, postIndex, false); }

  private setStar(field: 'starred' | 'readLater', map: Map<string, Set<number>>, feedId: string, postIndex: number, add: boolean): boolean {
    // Refuse new saves at the anon cap (removals and re-adds of existing entries always pass).
    if (add && !map.get(feedId)?.has(postIndex) && this.countStarMap(map) >= MAX_ANON_SAVED_POSTS)
      return false;
    if (add) {
      let set = map.get(feedId);
      if (!set) { set = new Set(); map.set(feedId, set); }
      set.add(postIndex);
    } else {
      const set = map.get(feedId);
      if (set) { set.delete(postIndex); if (!set.size) map.delete(feedId); }
    }
    if (this.data) {
      const key = `${feedId}-${postIndex}`;
      const entries = field == 'starred' ? this.data.starred : this.data.readLater;
      if (add) { if (!entries.includes(key)) entries.push(key); }
      else { const idx = entries.indexOf(key); if (idx >= 0) entries.splice(idx, 1); }
      const now = new Date().toISOString();
      if (field == 'starred') this.data.starredUpdate = now; else this.data.readLaterUpdate = now;
    }
    this.persistStars();
    this.notifyStarChange();
    return true;
  }

  private parseStarData(): void {
    if (!this.data) return;
    this.parsedStarred = this.parseStarArray(this.data.starred);
    this.parsedReadLater = this.parseStarArray(this.data.readLater);
    this.notifyStarChange();
  }

  private parseStarArray(entries: string[] | undefined): Map<string, Set<number>> {
    const map = new Map<string, Set<number>>();
    if (!entries) return map;
    for (const entry of entries) {
      const dashIdx = entry.lastIndexOf('-');
      const feedId = entry.substring(0, dashIdx);
      const postIndex = parseInt(entry.substring(dashIdx + 1), 10);
      const set = map.get(feedId);
      if (set) set.add(postIndex); else map.set(feedId, new Set([postIndex]));
    }
    return map;
  }

  // Starred/read-later lists are synthesized entirely from local state (every referenced feed is
  // one the anon user follows). Kept compatible with the auth engine's RestStarList shape.
  async fetchStarredList(count?: number, sortOrder?: 'newestFirst' | 'oldestFirst'): Promise<RestStarList | null> {
    await this.ensureStarFeedsResolved('starred');
    return this.synthesizeStarList('starred', count, sortOrder);
  }

  async fetchReadLaterList(count?: number, sortOrder?: 'newestFirst' | 'oldestFirst'): Promise<RestStarList | null> {
    await this.ensureStarFeedsResolved('readlater');
    return this.synthesizeStarList('readlater', count, sortOrder);
  }

  // Best-effort: fetch metadata for any saved-list feed we can't resolve (e.g. unsubscribed before
  // its metadata was persisted), so getFeed — and thus the saved-list headlines — resolve once the
  // public endpoint is reachable. Failures are swallowed; the list still renders what it can.
  private async ensureStarFeedsResolved(listId: 'starred' | 'readlater'): Promise<void> {
    if (!this.data) return;
    const source = listId == 'starred' ? this.data.starred : this.data.readLater;
    const missing = new Set<string>();
    for (const entry of source) {
      const dash = entry.lastIndexOf('-');
      if (dash > 0) { const id = entry.substring(0, dash); if (!this.getRawFeed(id)) missing.add(id); }
    }
    if (!missing.size) return;
    await Promise.all(Array.from(missing).map(id => this.ensureFeedInfo(id).catch(() => undefined)));
    this.persistStarFeedMeta();
  }

  private synthesizeStarList(listId: 'starred' | 'readlater', count?: number, sortOrder?: 'newestFirst' | 'oldestFirst'): RestStarList | null {
    if (!this.data) return null;
    const isStarred = listId == 'starred';
    const source = isStarred ? this.data.starred : this.data.readLater;
    const lastUpdate = isStarred ? this.data.starredUpdate : this.data.readLaterUpdate;
    const order = sortOrder ?? 'newestFirst';
    const ordered = order == 'newestFirst' ? [...source].reverse() : [...source];
    const entries = ordered.slice(0, Math.min(count ?? 1000, 1000));
    const feeds: Record<string, RestUserFeed> = {};
    for (const entry of entries) {
      const dashIdx = entry.lastIndexOf('-');
      if (dashIdx < 0) continue;
      const feed = this.getFeed(entry.substring(0, dashIdx));
      if (feed) feeds[feed.id] = feed;
    }
    return { entries, totalCount: Math.min(source.length, 1000), lastUpdate, feeds };
  }

  // --- Cross-feed unread list (computed locally) ---

  async fetchUnreadFeed(sortOrder: 'newestFirst' | 'oldestFirst', count = 1000, folder?: string): Promise<RestUnreadFeed | null> {
    return this.computeUnread(sortOrder, count, folder);
  }

  private computeUnread(sortOrder: 'newestFirst' | 'oldestFirst', count: number, folder?: string): RestUnreadFeed {
    // With a folder, only that folder's feeds are included; unknown folder → empty list
    const folderSet = folder != undefined ? new Set(this.data?.folders?.[folder] ?? []) : undefined;
    const collected: { feedId: string; postIndex: number; semi: number }[] = [];
    for (const feed of this.data?.feeds ?? []) {
      if (folderSet && !folderSet.has(feed.id)) continue;
      const semis = feed.recentPostsSemi;
      const postCount = feed.postCount;
      if (!semis?.length || !postCount) continue;
      const readSet = this.markerEngine.getReadForFeed(feed.id);
      // firstArchivedPostIndex isn't exposed on RestUserFeed; the trackable-window floor below bounds it.
      const firstArchived = (feed as { firstArchivedPostIndex?: number }).firstArchivedPostIndex ?? 0;
      const minIndex = Math.max(firstArchived, postCount - MAX_TRACKED_POSTS);
      for (let i = minIndex; i < postCount; i++) {
        if (readSet.has(i)) continue;
        const arrayPos = postCount - 1 - i; // recentPostsSemi is newest-first
        if (arrayPos < 0 || arrayPos >= semis.length) continue;
        collected.push({ feedId: feed.id, postIndex: i, semi: semis[arrayPos] });
      }
    }
    collected.sort((a, b) => sortOrder == 'newestFirst'
      ? (b.semi - a.semi) || (a.feedId < b.feedId ? -1 : a.feedId > b.feedId ? 1 : b.postIndex - a.postIndex)
      : (a.semi - b.semi) || (a.feedId < b.feedId ? -1 : a.feedId > b.feedId ? 1 : a.postIndex - b.postIndex));
    const page = collected.slice(0, count);
    const feeds: Record<string, RestUserFeed> = {};
    for (const e of page) { const f = this.getFeed(e.feedId); if (f) feeds[e.feedId] = f; }
    const semisOnly = page.map(e => e.semi);
    return {
      entries: page.map(e => `${e.feedId}-${e.postIndex}`),
      serverTime: this.lastServerTime,
      feeds,
      oldestSemi: semisOnly.length ? Math.min(...semisOnly) : undefined,
      newestSemi: semisOnly.length ? Math.max(...semisOnly) : undefined,
      // No cursor: the anon list is capped (≤MAX_ANON_FEEDS × ANON_SEMI_CAP posts) and returned in
      // one page. Posts older than the per-feed cap can't be placed — the documented end of the list.
      cursor: undefined,
    };
  }

  getUnreadFeedEntries(sortOrder: 'newestFirst' | 'oldestFirst', folder?: string): string[] | null {
    return this.computeUnread(sortOrder, 1000, folder).entries;
  }

  getUnreadFeedInfo(sortOrder: 'newestFirst' | 'oldestFirst', folder?: string): { hasMore: boolean; totalLoaded: number } | null {
    const entries = this.computeUnread(sortOrder, 1000, folder).entries;
    return { hasMore: false, totalLoaded: entries.length };
  }

  clearUnreadFeedCache(_sortOrder?: 'newestFirst' | 'oldestFirst', _folder?: string): void { /* nothing cached; computed on demand */ }
}
